// Implements: F-UIX-019, F-IDN-027
/**
 * Plataforma > Organizaciones: the list (with `member_count`) and its three writes, each sent once
 * and followed by a fresh read of the list (and of the platform totals' names, which name the
 * same organizations).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { OrganizationList, OrganizationStatus, OrganizationView } from "../../../types/organization";
import {
  createOrganization,
  fetchOrganizations,
  renameOrganization,
  setOrganizationStatus,
} from "../api/organizationApi";
import { ORGANIZATIONS_KEY } from "./keys";

export function useOrganizationList(): UseQueryResult<OrganizationList, ApiError> {
  return useQuery<OrganizationList, ApiError>({
    queryKey: ORGANIZATIONS_KEY,
    queryFn: ({ signal }) => fetchOrganizations(signal),
  });
}

function useListWrite<TVariables>(
  run: (variables: TVariables) => Promise<OrganizationView>,
): UseMutationResult<OrganizationView, ApiError, TVariables> {
  const queryClient = useQueryClient();
  return useMutation<OrganizationView, ApiError, TVariables>({
    mutationFn: run,
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: ["admin", "organizations"] }),
      ]),
  });
}

export function useCreateOrganization(): UseMutationResult<OrganizationView, ApiError, string> {
  return useListWrite((name: string) => createOrganization(name));
}

export interface Rename {
  readonly id: string;
  readonly name: string;
}

export function useRenameOrganization(): UseMutationResult<OrganizationView, ApiError, Rename> {
  return useListWrite(({ id, name }: Rename) => renameOrganization(id, name));
}

export interface StatusChange {
  readonly id: string;
  readonly status: OrganizationStatus;
}

export function useSetOrganizationStatus(): UseMutationResult<OrganizationView, ApiError, StatusChange> {
  return useListWrite(({ id, status }: StatusChange) => setOrganizationStatus(id, status));
}
